import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { Observable, ReplaySubject, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { flatMap } from 'lodash-es';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { FilterBarTranslations } from '../../base/entities/filter-bar-translations';
import { QueryPart } from '../../base/entities/query-part.class';
import { IFilterData } from '../../entities/filter-data.entity';
import { Filter } from '../../entities/filter.class';
import { FilterHelper } from '../../entities/filter.helper';
import { ShGenericFilterItemComponent } from '../../base/components/filter-item/filter-item.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'sh-filter-bar',
    templateUrl: './filter-bar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ShGenericFilterItemComponent, AsyncPipe]
})
export class ShFilterBarComponent extends BaseContainer implements OnInit, OnDestroy {
    public readonly filters$: ReplaySubject<Filter[]> = new ReplaySubject<Filter[]>(1);
    public readonly emit$: Subject<void> = new Subject<void>();

    private filterData$!: Observable<IFilterData[]>;

    @Input() set filters(filters: Filter[]) {
        this.filters$.next(filters);
    }

    @Output() filterValuesChanged: EventEmitter<QueryPart[]> = new EventEmitter<QueryPart[]>();

    @Input() i18n?: FilterBarTranslations;

    constructor() {
        super();
    }

    public ngOnInit(): void {
        this.initFilterData();
    }

    private initFilterData(): void {
        this.filterData$ = this.filters$.pipe(map(filters => filters.map(filter => filter.filterData)));
    }

    private emit(filterData: IFilterData[]): void {
        const queryParts: QueryPart[] = flatMap(filterData, filter => FilterHelper.getQueryPart(filter));
        return this.filterValuesChanged.emit(queryParts);
    }

    public onFilterChange(changedFilterData: IFilterData): void {
        this.filterData$
            .pipe(
                take(1),
                map(filterData =>
                    filterData.map(filter =>
                        FilterHelper.isSimilar(filter, changedFilterData) ? changedFilterData : filter
                    )
                ),
                takeUntil(this.destroy$)
            )
            .subscribe(filterData => this.emit(filterData));
    }

    public onRemoveFilter(filterToRemove: IFilterData): void {
        this.filterData$
            .pipe(
                take(1),
                map(filterData => filterData.filter(filter => !FilterHelper.isSimilar(filter, filterToRemove))),
                takeUntil(this.destroy$)
            )
            .subscribe(filterData => this.emit(filterData));
    }
}
